import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation } from "react-router-dom";
import { AppSidebar } from "@/components/shared/MainNavigation";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import PracticeSelector from "@/components/shared/TopPagePracticeSelector";
import NotesPanel from "@/components/shared/GlobalNotesPanel";
import { Menu } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { YearSelector } from "@/components/shared/YearSelector";
import { ProgramSwitcherPills, AcoContextChips } from "@/components/shared/ProgramSwitcher";
import { programContextForPath, setHomeProgram } from "@/platform/activeProgram";
import { useActiveProgram } from "@/platform/useActiveProgram";
import { NoPracticeState } from "@/components/shared/NoPracticeState";
import { prefetchAdminData } from "@/lib/admin-cache";
import { useQueryClient } from "@tanstack/react-query";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

// Import pages to keep mounted
import QualityWrapper from "@/pages/programs/mips/gap-management/QualityWrapper";
import MeasureSelection from "@/pages/programs/mips/Configuration";

export default function DashboardLayout() {
  // Add mount/unmount tracking
  useEffect(() => {
    console.log('[MOUNT/UNMOUNT] DashboardLayout MOUNTED at', new Date().toISOString());
    return () => console.log('[MOUNT/UNMOUNT] DashboardLayout UNMOUNTED at', new Date().toISOString());
  }, []);
  
  const [showNotesPagesList, setShowNotesPagesList] = useState(true);
  const [notesOpen, setNotesOpen] = useState(false);
  const { selectedPractice, user, token } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Warm every Admin tab's data right after an admin logs in, so the first
  // visit to Admin renders instantly (no spinners) — see admin-cache.ts.
  useEffect(() => {
    if (user?.role === 'admin' && token) prefetchAdminData(token, queryClient);
  }, [user, token, queryClient]);
  // APP context = path is APP Plus, OR any screen while the session program is
  // APP Plus (keeps ACO chips on Home / Data Management / Patients / Admin).
  const inAppContext = useActiveProgram() === 'app-plus';

  // SINGLE SOURCE OF TRUTH for the session program (D12 bug fix, 2026-07-14):
  // ANY route inside a program (sidebar link, top-bar switcher, Home picker,
  // deep link) updates the session program signal. Neutral routes keep the
  // LAST program — scopes upload targets AND which program's nav the sidebar
  // keeps showing (owner 2026-07-21).
  useEffect(() => {
    const ctx = programContextForPath(location.pathname);
    if (ctx === 'app-plus' || ctx === 'mips') {
      setHomeProgram(ctx);
    }
  }, [location.pathname]);
  
  // Determine which kept-mounted page is active
  const isGapManagementActive = location.pathname.includes('/programs/mips/gap-management');
  const isConfigurationActive = location.pathname === '/measure-selection';
  const isKeptMountedPage = isGapManagementActive || isConfigurationActive;
  
  // Only render kept-mounted pages if a practice is selected — OR when the URL
  // carries an explicit ?practiceId= override (D12: APP Plus "View Details"
  // opens the Detail page at the ACO's shadow practice without a MIPS selection).
  const hasPractice = !!selectedPractice?.id
    || new URLSearchParams(location.search).has('practiceId');
  
  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <SidebarInset>
          <header className="sticky top-0 z-20 flex h-14 items-center gap-2 sm:gap-3 border-b bg-background px-3 sm:px-4">
            <SidebarTrigger className="flex-shrink-0" />
            <Separator orientation="vertical" className="h-6" />
            
            {/* Logo/Title - Always visible */}
            <NavLink 
              to="/" 
              className="font-semibold text-foreground hover:text-primary transition-colors flex-shrink-0 text-sm sm:text-base" 
              aria-label="Go to Landing"
            >
              MIPS QCDR
            </NavLink>
            
            {/* Large Desktop view (1536px+) - Everything visible */}
            <div className="hidden 2xl:flex items-center gap-3 flex-1">
              <Separator orientation="vertical" className="h-6" />
              <div className="flex-1">
                {inAppContext ? <AcoContextChips /> : <PracticeSelector />}
              </div>
              
              {/* Viewing Year selector */}
              <YearSelector />
              
              <ProgramSwitcherPills />
              
              <NotesPanel 
                showPagesList={showNotesPagesList} 
                onTogglePagesList={() => setShowNotesPagesList(!showNotesPagesList)}
                onOpenChange={setNotesOpen}
              />
            </div>
            
            {/* Laptop/Desktop view (1024px-1536px) - Condensed for YOUR laptop */}
            <div className="hidden lg:flex 2xl:hidden items-center gap-2 flex-1">
              <Separator orientation="vertical" className="h-6 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                {inAppContext ? <AcoContextChips /> : <PracticeSelector />}
              </div>
              
              {/* Viewing Year selector */}
              <div className="flex-shrink-0">
                <YearSelector />
              </div>
              
              <div className="flex-shrink-0">
                <ProgramSwitcherPills />
              </div>
              
              <div className="flex-shrink-0">
                <NotesPanel 
                  showPagesList={showNotesPagesList} 
                  onTogglePagesList={() => setShowNotesPagesList(!showNotesPagesList)}
                  onOpenChange={setNotesOpen}
                />
              </div>
            </div>
            
            {/* Tablet view (768px-1024px) - More condensed */}
            <div className="hidden md:flex lg:hidden items-center gap-2 flex-1 justify-end">
              {inAppContext ? <AcoContextChips /> : <PracticeSelector />}
              <ProgramSwitcherPills />
              <NotesPanel 
                showPagesList={showNotesPagesList} 
                onTogglePagesList={() => setShowNotesPagesList(!showNotesPagesList)}
                onOpenChange={setNotesOpen}
              />
            </div>
            
            {/* Mobile menu (< 768px) */}
            <div className="md:hidden ml-auto">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8">
                    <Menu className="h-4 w-4" />
                    <span className="sr-only">Menu</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-sm text-muted-foreground">
                    <PracticeSelector />
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setNotesOpen(true)}>
                    Notes
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>
          <main className="flex-1 p-6">
            {/* Keep Gap Management mounted - show/hide based on route */}
            {hasPractice && (
              <div style={{ display: isGapManagementActive ? 'block' : 'none' }}>
                <QualityWrapper />
              </div>
            )}
            
            {/* Keep Configuration mounted - show/hide based on route */}
            {hasPractice && (
              <div style={{ display: isConfigurationActive ? 'block' : 'none' }}>
                <MeasureSelection />
              </div>
            )}

            {/* No practice: the kept-mounted pages are NOT rendered (the guard
                above), which used to leave these routes completely BLANK.
                Show the standard empty state instead. */}
            {isKeptMountedPage && !hasPractice && <NoPracticeState />}
            
            {/* Outlet for all other pages (not the kept-mounted ones) */}
            {!isKeptMountedPage && <Outlet />}
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
}
